import { useEffect, useState } from "react";
import { api, fmtMoney, fmtDate, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Pencil, Trash2 } from "lucide-react";
import StatusPill from "@/components/StatusPill";
import { useAuth } from "@/contexts/AuthContext";

const EMPTY = { card_id: "", transaction_date: "", merchant: "", amount: 0, reason: "", complaint_ref: "", expected_resolution: "", status: "Open", remarks: "" };

export default function Disputes() {
    const { hasRole } = useAuth();
    const [cards, setCards] = useState([]);
    const [rows, setRows] = useState([]);
    const [open, setOpen] = useState(false);
    const [f, setF] = useState(EMPTY);
    const [editingId, setEditingId] = useState(null);

    const load = () => api.get("/disputes").then((r) => setRows(r.data));
    useEffect(() => {
        api.get("/cards").then((r) => setCards(r.data));
        load();
    }, []);

    const set = (k) => (v) => setF((s) => ({ ...s, [k]: v }));
    const setInput = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));

    const submit = async (e) => {
        e.preventDefault();
        try {
            const payload = { ...f, amount: Number(f.amount || 0) };
            if (editingId) {
                await api.patch(`/disputes/${editingId}`, payload);
                toast.success("Dispute updated");
            } else {
                await api.post("/disputes", payload);
                toast.success("Dispute raised");
            }
            setOpen(false);
            setEditingId(null);
            setF(EMPTY);
            load();
        } catch (err) {
            toast.error(formatApiError(err));
        }
    };

    const openAdd = () => { setEditingId(null); setF(EMPTY); setOpen(true); };
    const openEdit = (row) => {
        setEditingId(row.id);
        setF({
            card_id: row.card_id || "",
            transaction_date: (row.transaction_date || "").slice(0, 10),
            merchant: row.merchant || "",
            amount: row.amount || 0,
            reason: row.reason || "",
            complaint_ref: row.complaint_ref || "",
            expected_resolution: (row.expected_resolution || "").slice(0, 10),
            status: row.status || "Open",
            remarks: row.remarks || "",
        });
        setOpen(true);
    };

    const del = async (id, merchant) => {
        if (!window.confirm(`Delete dispute for "${merchant}"?`)) return;
        try {
            await api.delete(`/disputes/${id}`);
            toast.success("Dispute deleted");
            load();
        } catch (err) { toast.error(formatApiError(err)); }
    };

    const updateStatus = async (id, status) => {
        try {
            await api.patch(`/disputes/${id}`, { status });
            load();
        } catch (err) {
            toast.error(formatApiError(err));
        }
    };

    const cardMap = Object.fromEntries(cards.map((c) => [c.id, c]));

    const statusToPill = (s) => (s === "Resolved" || s === "Refunded" ? "Paid" : s === "Rejected" ? "Overdue" : s === "Under Review" ? "Due Soon" : "Not Due");

    return (
        <div data-testid="disputes-page">
            <div className="flex items-end justify-between flex-wrap gap-4 mb-6">
                <div>
                    <div className="eyebrow">Disputes &amp; Chargebacks</div>
                    <h1 className="font-display text-4xl sm:text-5xl text-stone-900 leading-none mt-2">Track and recover.</h1>
                    <p className="text-sm text-stone-500 mt-3">{rows.length} disputes on file · {rows.filter(r => r.status === "Open").length} open</p>
                </div>
                {hasRole("admin", "finance") && (
                    <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setEditingId(null); setF(EMPTY); } }}>
                        <DialogTrigger asChild>
                            <Button className="bg-stone-900 hover:bg-stone-800 text-white" data-testid="add-dispute-btn" onClick={openAdd}><Plus size={16} className="mr-1.5" /> Raise dispute</Button>
                        </DialogTrigger>
                        <DialogContent>
                            <DialogHeader><DialogTitle className="font-display text-2xl">{editingId ? "Edit dispute" : "New dispute"}</DialogTitle></DialogHeader>
                            <form onSubmit={submit} className="space-y-3">
                                <div className="grid grid-cols-2 gap-3">
                                    <div className="col-span-2">
                                        <Label className="eyebrow">Card</Label>
                                        <Select value={f.card_id} onValueChange={set("card_id")}>
                                            <SelectTrigger className="mt-1.5"><SelectValue placeholder="Select card" /></SelectTrigger>
                                            <SelectContent>
                                                {cards.map((c) => <SelectItem key={c.id} value={c.id}>{c.bank_name} · •••• {c.last_4}</SelectItem>)}
                                            </SelectContent>
                                        </Select>
                                    </div>
                                    <div><Label className="eyebrow">Transaction Date</Label><Input required type="date" value={f.transaction_date} onChange={setInput("transaction_date")} className="mt-1.5" /></div>
                                    <div><Label className="eyebrow">Amount</Label><Input required type="number" value={f.amount} onChange={setInput("amount")} className="mt-1.5" /></div>
                                    <div className="col-span-2"><Label className="eyebrow">Merchant</Label><Input required value={f.merchant} onChange={setInput("merchant")} className="mt-1.5" /></div>
                                    <div className="col-span-2"><Label className="eyebrow">Reason</Label><Textarea required rows={2} value={f.reason} onChange={setInput("reason")} className="mt-1.5" /></div>
                                    <div><Label className="eyebrow">Complaint Ref</Label><Input value={f.complaint_ref} onChange={setInput("complaint_ref")} className="mt-1.5" /></div>
                                    <div><Label className="eyebrow">Expected Resolution</Label><Input type="date" value={f.expected_resolution} onChange={setInput("expected_resolution")} className="mt-1.5" /></div>
                                </div>
                                <Button type="submit" className="w-full bg-stone-900 hover:bg-stone-800 text-white">{editingId ? "Save changes" : "Raise dispute"}</Button>
                            </form>
                        </DialogContent>
                    </Dialog>
                )}
            </div>

            <div className="bg-white border border-stone-200 rounded-lg overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="dense-table w-full">
                        <thead>
                            <tr><th>Raised</th><th>Card</th><th>Merchant</th><th className="text-right">Amount</th><th>Reason</th><th>Ref</th><th>Status</th><th></th></tr>
                        </thead>
                        <tbody>
                            {rows.map((d) => {
                                const c = cardMap[d.card_id];
                                return (
                                    <tr key={d.id}>
                                        <td className="tabular-nums">{fmtDate(d.raised_date)}</td>
                                        <td>{c ? `${c.bank_name} · •••• ${c.last_4}` : "—"}</td>
                                        <td className="font-medium">{d.merchant}</td>
                                        <td className="text-right tabular-nums">{fmtMoney(d.amount)}</td>
                                        <td className="text-xs max-w-[280px] truncate">{d.reason}</td>
                                        <td className="font-mono-figure text-xs">{d.complaint_ref || "—"}</td>
                                        <td>
                                            {hasRole("admin", "finance") ? (
                                                <Select value={d.status} onValueChange={(v) => updateStatus(d.id, v)}>
                                                    <SelectTrigger className="h-8 w-[140px] text-xs"><SelectValue /></SelectTrigger>
                                                    <SelectContent>
                                                        <SelectItem value="Open">Open</SelectItem>
                                                        <SelectItem value="Under Review">Under Review</SelectItem>
                                                        <SelectItem value="Resolved">Resolved</SelectItem>
                                                        <SelectItem value="Rejected">Rejected</SelectItem>
                                                        <SelectItem value="Refunded">Refunded</SelectItem>
                                                    </SelectContent>
                                                </Select>
                                            ) : (
                                                <StatusPill status={statusToPill(d.status)} />
                                            )}
                                        </td>
                                        <td className="text-right">
                                            {hasRole("admin", "finance") && (
                                                <div className="inline-flex items-center gap-1">
                                                    <button onClick={() => openEdit(d)} title="Edit dispute"
                                                            className="p-1.5 rounded hover:bg-stone-100 text-stone-500 hover:text-stone-900"
                                                            data-testid={`dispute-edit-${d.id}`}>
                                                        <Pencil size={13} />
                                                    </button>
                                                    {hasRole("admin") && (
                                                        <button onClick={() => del(d.id, d.merchant)} title="Delete dispute"
                                                                className="p-1.5 rounded hover:bg-red-50 text-stone-500 hover:text-red-600"
                                                                data-testid={`dispute-delete-${d.id}`}>
                                                            <Trash2 size={13} />
                                                        </button>
                                                    )}
                                                </div>
                                            )}
                                        </td>
                                    </tr>
                                );
                            })}
                            {rows.length === 0 && <tr><td colSpan={8} className="text-center text-stone-500 py-14">No disputes.</td></tr>}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
}
